import { dataGetCommonStructure } from 'utils/data/data';
import { __t, applyTranslations } from 'utils/language';
import { INFLUENCER_TYPES } from 'utils/influencers';
import {
  CHANNEL_TYPE_BLOG,
  CHANNEL_TYPE_FACEBOOK,
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_PINTEREST,
  CHANNEL_TYPE_SNAPCHAT,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_TWITTER,
  CHANNEL_TYPE_YOUTUBE
} from 'utils/channels';
import {
  ENGAGEMENT,
  FOLLOWERS,
  GENDER_RATIO,
  VIEWS
} from 'modules/discovery/creators/constants/filters';

import type {
  DataStructureCommon,
  DiscoveryCreatorsCategories,
  DiscoveryCreatorsFilterAudience,
  DiscoveryCreatorsFilterAudienceTikTok,
  DiscoveryCreatorsFilterEngagement,
  DiscoveryCreatorsFilterFollowers,
  DiscoveryCreatorsFilterKeywords,
  DiscoveryCreatorsFilterViews,
  DiscoveryCreatorsFilters,
  DiscoveryFilterNumber,
  DiscoveryFilterProfiles
} from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./filters.i18n.json'));
applyTranslations(require('locales/common/collabary-channels.i18n.json'));
applyTranslations(require('components/influencer-verification-label/influencer-verification-label.i18n.json'));

const ALL_CHANNELS = [
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_YOUTUBE,
  CHANNEL_TYPE_BLOG,
  CHANNEL_TYPE_PINTEREST,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_FACEBOOK,
  CHANNEL_TYPE_TWITTER,
  CHANNEL_TYPE_SNAPCHAT
];

const ALL_PROFILES = ['verified', 'unverified'];
const CREDIBILITY_LEVELS = ['excellent', 'very_good', 'good', 'acceptable'];
const REACHABILITY_LEVELS = ['excellent', 'very_good', 'good', 'acceptable'];
const PROFESSIONS = ['actor', 'musician', 'photographer', 'model'];
const BODY_ATTRIBUTES = ['thin', 'muscular', 'curvy', 'petite', 'tall', 'tattoos', 'freckles', 'piercings', 'plus_size'];
const ETHNIC_GROUPS = [
  'asian',
  'black',
  'hispanic',
  'middle_eastern',
  'multiracial',
  'white',
  'no_answer'
];
const STYLES = [
  'mainstream',
  'premium',
  'sport',
  'retro',
  'scandi_style',
  'streetwear'
];

const FILTERS_MSG_KEY = 'discovery_creators_filters';
const CHANNELS_MSG_KEY = 'collabary_channels';
const COMMON_MSG_KEY = 'collabary_common';
const PROFILES_MSG_KEY = 'influencer_verification_label.display_profiles';

const AGE_ENTRIES = [
  {
    min: 13,
    max: 17,
    id: '13to17',
    range: '13-17'
  },
  {
    min: 18,
    max: 24,
    id: '18to24',
    range: '18-24'
  },
  {
    min: 25,
    max: 34,
    id: '25to34',
    range: '25-34'
  },
  {
    min: 35,
    max: 44,
    id: '35to44',
    range: '35-44'
  },
  {
    min: 45,
    max: 64,
    id: '45to64',
    range: '45-64'
  },
  {
    min: 65,
    max: 75,
    id: '65plus',
    range: '65+'
  }
];

const age: DiscoveryFilterNumber = {
  min: 13,
  max: 75,
  from: 13,
  to: 75
};

export const audience: DiscoveryCreatorsFilterAudience = {
  audienceAges: AGE_ENTRIES.map<DataStructureCommon>(obj => (
    dataGetCommonStructure(obj.id, obj.range, obj.id, false)
  )),
  audienceAgesShare: {
    checkbox: dataGetCommonStructure('audience-ages-share', '', 'checkbox', false),
    value: ''
  },
  audienceCities: [],
  audienceCountries: [],
  audienceCountriesShare: {
    checkbox: dataGetCommonStructure('audience-countries-share', '', 'checkbox', false),
    value: ''
  },
  audienceCredibility: CREDIBILITY_LEVELS.map<DataStructureCommon>(value => (
    dataGetCommonStructure(value, __t(`collabary_common.score_label.${value}`), value, false)
  )),
  audienceReachability: REACHABILITY_LEVELS.map<DataStructureCommon>(value => (
    dataGetCommonStructure(value, __t(`collabary_common.score_label.${value}`), value, false)
  )),
  audienceGenderRatio: {
    female: {
      min: GENDER_RATIO.MIN,
      max: GENDER_RATIO.MAX,
      from: 0,
      to: 100
    },
    male: {
      min: GENDER_RATIO.MIN,
      max: GENDER_RATIO.MAX,
      from: 0,
      to: 100
    }
  },
  audienceInterests: []
};

export const audienceTikTok: DiscoveryCreatorsFilterAudienceTikTok = {
  audienceAgesTikTok: AGE_ENTRIES.map<DataStructureCommon>(obj => (
    dataGetCommonStructure(obj.id, obj.range, obj.id, false)
  )),
  audienceAgesShareTikTok: {
    checkbox: dataGetCommonStructure('audience-ages-share-tiktok', '', 'checkbox', false),
    value: ''
  },
  audienceCountriesTikTok: [],
  audienceCountriesShareTikTok: {
    checkbox: dataGetCommonStructure('audience-countries-share-tiktok', '', 'checkbox', false),
    value: ''
  },
  audienceReachabilityTikTok: REACHABILITY_LEVELS.map<DataStructureCommon>(value => (
    dataGetCommonStructure(value, __t(`collabary_common.score_label.${value}`), value, false)
  )),
  audienceGenderRatioTikTok: {
    female: {
      min: GENDER_RATIO.MIN,
      max: GENDER_RATIO.MAX,
      from: 0,
      to: 100
    },
    male: {
      min: GENDER_RATIO.MIN,
      max: GENDER_RATIO.MAX,
      from: 0,
      to: 100
    }
  }
};

const channels: Array<DataStructureCommon> = ALL_CHANNELS.map<DataStructureCommon>(channel => {
  const isInstagram = channel === 'instagram';

  return dataGetCommonStructure(channel, __t(`${CHANNELS_MSG_KEY}.${channel}.label`), String(isInstagram), isInstagram);
});

const engagement: DiscoveryCreatorsFilterEngagement = {
  instagram: {
    min: ENGAGEMENT.MIN_INSTAGRAM, // block influencers with 0% engagement
    max: ENGAGEMENT.MAX_INSTAGRAM,
    from: 0.1,
    to: 35
  },
  youtube: {
    min: ENGAGEMENT.MIN_REST,
    max: ENGAGEMENT.MAX_REST,
    from: 0,
    to: 100
  },
  tiktok: {
    min: ENGAGEMENT.MIN_REST,
    max: ENGAGEMENT.MAX_REST,
    from: 0,
    to: 100
  }
};

const followers: DiscoveryCreatorsFilterFollowers = {
  instagram: {
    min: 1,
    max: 100,
    from: 1,
    to: 100,
    transformed: {
      min: FOLLOWERS.MIN,
      max: FOLLOWERS.MAX
    },
    tiers: {
      enabled: false,
      data: INFLUENCER_TYPES.map(tier => (
        dataGetCommonStructure(tier, __t(`${FILTERS_MSG_KEY}.tiers.${tier}`), tier, false)
      ))
    }
  },
  youtube: {
    min: 1,
    max: 100,
    from: 1,
    to: 100,
    transformed: {
      min: FOLLOWERS.MIN,
      max: FOLLOWERS.MAX
    }
  },
  tiktok: {
    min: 1,
    max: 100,
    from: 1,
    to: 100,
    transformed: {
      min: FOLLOWERS.MIN,
      max: FOLLOWERS.MAX
    }
  }
};

const genders: Array<DataStructureCommon> = [
  dataGetCommonStructure('female', __t(`${COMMON_MSG_KEY}.gender.f`), 'F', false),
  dataGetCommonStructure('male', __t(`${COMMON_MSG_KEY}.gender.m`), 'M', false),
  dataGetCommonStructure('mixed-gender', __t(`${COMMON_MSG_KEY}.gender.g`), 'G', false),
  dataGetCommonStructure('non-binary', __t(`${COMMON_MSG_KEY}.gender.n`), 'N', false)
];

const keywords: DiscoveryCreatorsFilterKeywords = {
  included: [],
  excluded: [],
  matchMode: 'and'
};

const profiles: DiscoveryFilterProfiles = ALL_PROFILES.map<DataStructureCommon>(key => (
  dataGetCommonStructure(
    key,
    __t(`${PROFILES_MSG_KEY}.${key}`),
    String(key === 'verified'),
    key === 'verified'
  )
));

const views: DiscoveryCreatorsFilterViews = {
  youtube: {
    min: 0,
    max: 100,
    from: 0,
    to: 100,
    transformed: {
      min: VIEWS.MIN,
      max: VIEWS.MAX
    }
  },
  tiktok: {
    min: 0,
    max: 100,
    from: 0,
    to: 100,
    transformed: {
      min: VIEWS.MIN,
      max: VIEWS.MAX
    }
  }
};

const categories: DiscoveryCreatorsCategories = {
  items: [],
  matchMode: 'and'
};

const residual = {
  bodyAttributes: BODY_ATTRIBUTES.map<DataStructureCommon>(value => (
    dataGetCommonStructure(value, __t(`${FILTERS_MSG_KEY}.labels.body_attributes.${value}`), value, false)
  )),
  cities: [],
  countries: [],
  ethnicGroups: ETHNIC_GROUPS.map<DataStructureCommon>(value => (
    dataGetCommonStructure(value, __t(`${COMMON_MSG_KEY}.ethnic_group.${value}`), value, false)
  )),
  professions: PROFESSIONS.map<DataStructureCommon>(value => (
    dataGetCommonStructure(value, __t(`${COMMON_MSG_KEY}.jobs.${value}`), value, false)
  )),
  searchByName: '',
  styles: STYLES.map<DataStructureCommon>(value => (
    dataGetCommonStructure(value, __t(`${FILTERS_MSG_KEY}.labels.styles.${value}`), value, false)
  ))
};

export const DEFAULT_FILTERS: DiscoveryCreatorsFilters = {
  age,
  ...audience,
  ...audienceTikTok,
  categories,
  channels,
  engagement,
  followers,
  genders,
  keywords,
  profiles,
  views,
  ...residual
};
